// MonteCarloChart.tsx — the Monte-Carlo / Renditeband fan chart: median (p50) line plus
// a p10–p90 band (light fill) with a stronger p25–p75 band nested inside it, using
// uPlot's native `bands` feature (fills the area between two named series) rather than a
// hand-rolled canvas plugin. Reuses the same money-axis measuredAxisSize fix and zero
// line plugin as SimulationChart/CompareChart.
import { useCallback, useMemo, useState } from 'preact/hooks';
import type { Options } from 'uplot';
import type { MonteCarloBandPoint } from '@etf-cockpit/engine';
import { UplotBase } from './UplotBase.js';
import { formatCents, formatCentsCompact } from '../lib/format.js';
import { zeroLinePlugin, chartZoomPlugin } from '../lib/uplot-plugins.js';
import type { ZoomState } from '../lib/uplot-plugins.js';
import { measuredAxisSize } from '../lib/uplotAxis.js';
import { hexToRgba, readCssVar } from '../lib/theme.js';

export interface MonteCarloChartProps {
  band: MonteCarloBandPoint[];
}

// Palette (see src/styles/global.css): median = the current --accent theme preset (lib/
// theme.ts), band fills = that same accent at low opacity (p10–p90 lighter, p25–p75
// stronger, per the feature spec) — all derived from one computed color at build time so
// this chart recolors with Einstellungen -> Darstellung.

function toUnixSeconds(iso: string): number {
  return Math.floor(new Date(`${iso}T00:00:00Z`).getTime() / 1000);
}

type ChartData = [number[], number[], number[], number[], number[], number[]];

export function MonteCarloChart({ band }: MonteCarloChartProps) {
  const [zoom, setZoom] = useState<ZoomState | null>(null);
  const data = useMemo((): ChartData => {
    const x = band.map((p) => toUnixSeconds(p.date));
    const p90 = band.map((p) => p.p90);
    const p75 = band.map((p) => p.p75);
    const p50 = band.map((p) => p.p50);
    const p25 = band.map((p) => p.p25);
    const p10 = band.map((p) => p.p10);
    return [x, p90, p75, p50, p25, p10];
  }, [band]);

  const buildOptions = useCallback(
    (width: number): Options => {
      const accent = readCssVar('--accent', '#60a5fa');
      const fillOuter = hexToRgba(accent, 0.14); // p10–p90
      const fillInner = hexToRgba(accent, 0.28); // p25–p75
      const strokeOuter = hexToRgba(accent, 0.3); // p10/p90 boundary lines
      const strokeInner = hexToRgba(accent, 0.45); // p25/p75 boundary lines
      return {
        width,
        height: 220,
        padding: [8, 8, 0, 0],
        legend: { show: true },
        cursor: { drag: { x: true, y: true }, points: { size: 6 } },
        scales: {
          x: { time: true },
          y: {
            range: (_u, initMin, initMax) => [Math.min(0, initMin), Math.max(0, initMax)],
          },
        },
        axes: [
          {
            stroke: '#93a0b8',
            grid: { stroke: 'rgba(232,236,244,0.08)', width: 1 },
            ticks: { stroke: 'rgba(232,236,244,0.15)', width: 1 },
          },
          {
            stroke: '#93a0b8',
            grid: { stroke: 'rgba(232,236,244,0.08)', width: 1 },
            ticks: { stroke: 'rgba(232,236,244,0.15)', width: 1 },
            values: (_u, ticks) => ticks.map((t) => formatCentsCompact(t)),
            size: measuredAxisSize,
          },
        ],
        series: [
          { label: 'Datum' },
          {
            label: 'p90',
            stroke: strokeOuter,
            width: 1,
            points: { show: false },
            value: (_u, v) => (v == null ? '—' : formatCents(v)),
          },
          {
            label: 'p75',
            stroke: strokeInner,
            width: 1,
            points: { show: false },
            value: (_u, v) => (v == null ? '—' : formatCents(v)),
          },
          {
            label: 'Median (p50)',
            stroke: accent,
            width: 2,
            points: { show: false },
            value: (_u, v) => (v == null ? '—' : formatCents(v)),
          },
          {
            label: 'p25',
            stroke: strokeInner,
            width: 1,
            points: { show: false },
            value: (_u, v) => (v == null ? '—' : formatCents(v)),
          },
          {
            label: 'p10',
            stroke: strokeOuter,
            width: 1,
            points: { show: false },
            value: (_u, v) => (v == null ? '—' : formatCents(v)),
          },
        ],
        // Series indices: 0=x, 1=p90, 2=p75, 3=p50, 4=p25, 5=p10. Outer band first (drawn
        // underneath), inner band second (drawn on top, so it reads visually stronger even
        // though both use a flat fill — the overlap of the two doubles up its opacity).
        bands: [
          { series: [1, 5], fill: fillOuter },
          { series: [2, 4], fill: fillInner },
        ],
        plugins: [zeroLinePlugin(), chartZoomPlugin(setZoom)],
      };
    },
    [setZoom],
  );

  if (band.length === 0) {
    return <div class="empty-state">Keine Simulationsdaten vorhanden.</div>;
  }

  return (
    <div class="chart-wrap">
      <UplotBase data={data} buildOptions={buildOptions} height={220} />
      {zoom?.zoomed ? (
        <div class="chart-zoom-row">
          <button type="button" class="chart-zoom-reset" onClick={() => zoom.reset()}>
            Zoom zurücksetzen
          </button>
        </div>
      ) : null}
    </div>
  );
}
